import React, { useState, useEffect } from "react";
import {
  Building2, Hash, Calendar, Plus, Trash2, Loader2,
  CheckCircle, XCircle, ChevronDown, MapPin, Check, X, Pencil, Briefcase
} from "lucide-react";
import axios from "axios";
import CalendarInput from "../calendarInput";
import DoneByBadge from "./DoneByBatch";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const inputCls =
  "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-transparent transition disabled:bg-gray-50 disabled:cursor-not-allowed";

const InlineBanner = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 mt-2 ${
      type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
    }`}>
      {type === "success" ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 shrink-0" />}
      {msg}
    </div>
  );
};

const InfoPill = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded border border-gray-100">
      <Icon className="h-3 w-3 text-gray-400 shrink-0" />
      <span className="text-[10px] text-gray-500 shrink-0">{label}:</span>
      <span className="text-[10px] font-medium text-gray-700 truncate">{value}</span>
    </div>
  );
};

const newCompanyEntry = () => ({
  id: Date.now() + Math.random(),
  isPrefilled: false,
  isEditing: false,
  location: "",
  companyId: "",
  companyName: "",
  companyLocation: "",
  verificationStatus: "not_verified",
  employmentStatus: null,
  ecode: "",
  lastWorkingDate: "",
  savedStatus: null,
  isSaving: false,
  bannerMsg: "",
  bannerType: "success",
  doneByName: null,
  doneAt: null,
});

const employerToRow = (employer, index) => ({
  ...newCompanyEntry(),
  id: Date.now() + index + Math.random(),
  isPrefilled: true,
  isEditing: false,
  companyId: employer.companyId || "",
  companyName: employer.companyDetails?.name || employer.companyName || "",
  companyLocation: employer.companyDetails?.location || "",
  ecode: employer.ecode || "",
  lastWorkingDate: employer.lastWorkingDate ? employer.lastWorkingDate.split("T")[0] : "",
  verificationStatus: employer.isVerified === true ? "verified" : "not_verified",
  employmentStatus: employer.isEmployed === true ? "employed" : employer.isEmployed === false ? "not_employed" : null,
  savedStatus: employer.isVerified === true ? "verified" : employer.isVerified === false ? "not_verified" : null,
  // metadata from API
  doneByName: employer.doneByName || null,
  doneAt: employer.doneAt || null,
});

const useCompanyFlash = (setCompanies) =>
  (id, msg, type = "success") => {
    setCompanies(prev => prev.map(c => c.id === id ? { ...c, bannerMsg: msg, bannerType: type } : c));
    setTimeout(() => setCompanies(prev => prev.map(c => c.id === id ? { ...c, bannerMsg: "", bannerType: "success" } : c)), 3500);
  };

const CompanyCheck = ({ workerId, isUTVMember = false, initialEmployers = [], canEditVerification = false }) => {
  const [companies, setCompanies] = useState(() =>
    initialEmployers.length > 0 ? initialEmployers.map(employerToRow) : [newCompanyEntry()]
  );
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [companiesCache, setCompaniesCache] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState({});

  const flashCompany = useCompanyFlash(setCompanies);

  useEffect(() => {
    if (initialEmployers.length > 0) setCompanies(initialEmployers.map(employerToRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialEmployers)]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const token = localStorage.getItem("superadmintoken");
        const res = await axios.get(`${BASE_URL}/lenskart/locations`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success && Array.isArray(res.data.locations)) setLocations(res.data.locations);
      } catch { /* silent */ }
      finally { setLoadingLocations(false); }
    };
    fetchLocations();
  }, []);

  const fetchCompaniesForLocation = async (location, locationId, rowId) => {
    if (!location || companiesCache[location]) return;
    setLoadingCompanies(prev => ({ ...prev, [rowId]: true }));
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await axios.get(`${BASE_URL}/onDemandCompanies/list-companies/${locationId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && Array.isArray(res.data.companies))
        setCompaniesCache(prev => ({ ...prev, [location]: res.data.companies }));
    } catch { /* silent */ }
    finally { setLoadingCompanies(prev => ({ ...prev, [rowId]: false })); }
  };

  const update = (id, patch) => setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));

  const handleLocationSelect = (id, loc) => {
    const locationName = loc?.name || loc;
    update(id, { location: locationName, companyId: "", companyName: "", ecode: "", lastWorkingDate: "", savedStatus: null, verificationStatus: "not_verified", employmentStatus: null });
    if (locationName) fetchCompaniesForLocation(locationName, loc._id, id);
  };

  const handleCompanySelect = (id, companyId, companyName) => {
    update(id, { companyId, companyName, savedStatus: null, verificationStatus: "not_verified", employmentStatus: null });
  };

  const handleVerificationChange = (id, value) => {
    update(id, { verificationStatus: value, employmentStatus: value === "not_verified" ? null : "", ecode: "", lastWorkingDate: "", savedStatus: null });
  };

  const handleEmploymentStatus = (id, status) => update(id, { employmentStatus: status });

  const callAddEmployer = async (company, payload) => {
    const token = localStorage.getItem("superadmintoken");
    const res = await axios.post(
      `${BASE_URL}/lenskart-admin/addEmployer/${workerId}`,
      { employers: [payload] },
      { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
    );
    if (!res.data.success) throw new Error(res.data.message || "API returned failure");
    return res.data;
  };

  const handleSaveNotVerified = async (company) => {
    update(company.id, { isSaving: true });
    try {
      const res = await callAddEmployer(company, { companyId: company.companyId, isVerified: false });
      update(company.id, { isSaving: false, savedStatus: "not_verified", isPrefilled: true, isEditing: false, companyLocation: company.location, doneAt: new Date().toISOString(), doneByName: res.doneByName || null });
      flashCompany(company.id, "Saved as Not Verified.", "success");
    } catch (err) {
      update(company.id, { isSaving: false });
      flashCompany(company.id, err.response?.data?.message || err.message || "Failed. Try again.", "error");
    }
  };

  const handleSaveNotEmployedVerified = async (company) => {
    update(company.id, { isSaving: true });
    try {
      const res = await callAddEmployer(company, { companyId: company.companyId, isVerified: true, isEmployed: false });
      update(company.id, { isSaving: false, savedStatus: "verified", isPrefilled: true, isEditing: false, companyLocation: company.location, doneAt: new Date().toISOString(), doneByName: res.doneByName || null });
      flashCompany(company.id, "Saved as Not Employee & Verified.", "success");
    } catch (err) {
      update(company.id, { isSaving: false });
      flashCompany(company.id, err.response?.data?.message || err.message || "Failed. Try again.", "error");
    }
  };

  const handleSaveEmployedVerified = async (company) => {
    if (!company.ecode.trim()) { flashCompany(company.id, "Worker code is required", "error"); return; }
    update(company.id, { isSaving: true });
    try {
      const res = await callAddEmployer(company, { companyId: company.companyId, isVerified: true, isEmployed: true, ecode: company.ecode.trim(), ...(company.lastWorkingDate && { lastWorkingDate: company.lastWorkingDate }) });
      update(company.id, { isSaving: false, savedStatus: "verified", isPrefilled: true, isEditing: false, companyLocation: company.location, doneAt: new Date().toISOString(), doneByName: res.doneByName || null });
      flashCompany(company.id, "Saved as Is Employee & Verified.", "success");
    } catch (err) {
      update(company.id, { isSaving: false });
      flashCompany(company.id, err.response?.data?.message || err.message || "Failed. Try again.", "error");
    }
  };

  const handleEdit = (company) => {
    if (company.companyLocation) fetchCompaniesForLocation(company.companyLocation, company.id);
    update(company.id, { isPrefilled: false, isEditing: true, location: company.companyLocation || "" });
  };

  const addCompany = () => setCompanies(prev => [...prev, newCompanyEntry()]);
  const removeCompany = (id) => setCompanies(prev => prev.length === 1 ? prev : prev.filter(c => c.id !== id));

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
      <div className="flex items-center gap-1.5 mb-3 text-blue-600">
        <Building2 className="h-4 w-4" />
        <span className="text-xs font-semibold">Company Check</span>
      </div>

      <div className="space-y-3">
        {companies.map((company, index) => (
          <div key={company.id} className={`p-3 rounded-md border bg-white ${company.isPrefilled ? "border-gray-200" : "border-blue-100"}`}>

            {/* Card header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-semibold text-blue-600">Company {index + 1}</span>
                {company.savedStatus === "verified" && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                    <CheckCircle className="h-2.5 w-2.5" /> Verified
                  </span>
                )}
                {company.savedStatus === "not_verified" && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                    <XCircle className="h-2.5 w-2.5" /> Not Verified
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {company.isPrefilled && (
  <button
    type="button"
    onClick={canEditVerification ? () => handleEdit(company) : undefined}
    disabled={company.isSaving || !canEditVerification}
    title={!canEditVerification ? "Not permitted" : undefined}
    className={`flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border disabled:opacity-40 ${
      canEditVerification
        ? "text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-100"
        : "text-gray-300 border-gray-200 cursor-not-allowed"
    }`}
  >
    <Pencil className="h-2.5 w-2.5" /> Edit
  </button>
)}
                {!company.isPrefilled && !company.isEditing && companies.filter(c => !c.isPrefilled).length > 0 && (
                  <button type="button" onClick={() => removeCompany(company.id)} disabled={company.isSaving}
                    className="flex items-center gap-0.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-40">
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
            </div>

            {/* ══ PRE-FILLED SUMMARY VIEW ══ */}
            {company.isPrefilled ? (
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-1.5">
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded border border-blue-100">
                    <Building2 className="h-3 w-3 text-blue-400 shrink-0" />
                    <span className="text-[10px] font-medium text-blue-700 truncate">{company.companyName || company.companyId}</span>
                  </div>
                  {company.companyLocation && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded border border-gray-100">
                      <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="text-[10px] font-medium text-gray-600">{company.companyLocation}</span>
                    </div>
                  )}
                  {company.employmentStatus && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded border ${company.employmentStatus === "employed" ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-gray-100"}`}>
                      <Briefcase className={`h-3 w-3 shrink-0 ${company.employmentStatus === "employed" ? "text-emerald-500" : "text-gray-400"}`} />
                      <span className={`text-[10px] font-medium ${company.employmentStatus === "employed" ? "text-emerald-700" : "text-gray-600"}`}>
                        {company.employmentStatus === "employed" ? "Is Employee" : "Not Employee"}
                      </span>
                    </div>
                  )}
                  <InfoPill icon={Hash} label="Emp Code" value={company.ecode} />
                  <InfoPill icon={Calendar} label="Last Working" value={formatDate(company.lastWorkingDate)} />
                </div>

                {/* ── done-by INSIDE each employer's card ── */}
                <DoneByBadge doneByName={company.doneByName} doneAt={company.doneAt} label="Updated" />

                <InlineBanner msg={company.bannerMsg} type={company.bannerType} />
              </div>

            ) : (
            /* ══ NEW / EDIT FORM ══ */
            <>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {/* Location */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Location *</label>
                  <div className="relative">
                    <button type="button" onClick={() => document.getElementById(`loc-${company.id}`)?.classList.toggle("hidden")} disabled={loadingLocations}
                      className="w-full px-2 py-1.5 text-xs border rounded-md text-left flex justify-between items-center bg-white hover:border-blue-400 disabled:bg-gray-100">
                      <span className="flex items-center truncate">
                        <MapPin className="h-3 w-3 text-gray-400 mr-1 shrink-0" />
                        <span className="truncate">{company.location || "Select"}</span>
                      </span>
                      <ChevronDown className="h-3 w-3 text-gray-500 shrink-0" />
                    </button>
                    <div id={`loc-${company.id}`} className="absolute hidden z-50 mt-1 w-full bg-white shadow-lg rounded-md py-0.5 max-h-36 overflow-auto border">
                      <button type="button" onClick={() => { handleLocationSelect(company.id, ""); document.getElementById(`loc-${company.id}`)?.classList.add("hidden"); }} className="block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 text-gray-400">Clear</button>
                      {locations.map(loc => (
                        <button key={loc._id} type="button"
                          onClick={() => { handleLocationSelect(company.id, loc); document.getElementById(`loc-${company.id}`)?.classList.add("hidden"); }}
                          className={`block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 ${company.location === loc.name ? "bg-indigo-50 text-indigo-700 font-medium" : ""}`}
                        >{loc.name}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Company */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Company *</label>
                  <div className="relative">
                    <button type="button"
                      onClick={() => company.location && document.getElementById(`comp-${company.id}`)?.classList.toggle("hidden")}
                      disabled={!company.location || loadingCompanies[company.id]}
                      className={`w-full px-2 py-1.5 text-xs border rounded-md text-left flex justify-between items-center ${!company.location || loadingCompanies[company.id] ? "bg-gray-100 text-gray-400" : "bg-white hover:border-blue-400"}`}>
                      <span className="flex items-center truncate">
                        <Building2 className="h-3 w-3 text-gray-400 mr-1 shrink-0" />
                        {loadingCompanies[company.id]
                          ? <span className="flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Loading...</span>
                          : <span className="truncate">{company.companyName || "Select"}</span>}
                      </span>
                      <ChevronDown className="h-3 w-3 text-gray-500 shrink-0" />
                    </button>
                    <div id={`comp-${company.id}`} className="absolute hidden z-50 mt-1 w-full bg-white shadow-lg rounded-md py-0.5 max-h-36 overflow-auto border">
                      <button type="button" onClick={() => { handleCompanySelect(company.id, "", ""); document.getElementById(`comp-${company.id}`)?.classList.add("hidden"); }} className="block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 text-gray-400">Clear</button>
                      {companiesCache[company.location]?.map(comp => (
                        <button key={comp._id} type="button"
                          onClick={() => { handleCompanySelect(company.id, comp._id, comp.name); document.getElementById(`comp-${company.id}`)?.classList.add("hidden"); }}
                          className={`block w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 ${company.companyId === comp._id ? "bg-indigo-50 text-indigo-700 font-medium" : ""}`}
                        >{comp.name}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {company.companyId && (
                <div className="pt-2 border-t border-gray-100 mt-1 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Verification Status *</label>
                    <div className="relative">
                      <select value={company.verificationStatus} onChange={(e) => handleVerificationChange(company.id, e.target.value)} disabled={company.isSaving} className={`${inputCls} appearance-none pr-7 cursor-pointer`}>
                        <option value="not_verified">Not Verified</option>
                        <option value="verified">Verified</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {company.verificationStatus === "verified" && (
                    <div>
                      <p className="text-[10px] text-gray-500 mb-1.5 font-medium">Employment Status</p>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => handleEmploymentStatus(company.id, "employed")} disabled={company.isSaving}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border transition disabled:opacity-50 disabled:cursor-not-allowed ${company.employmentStatus === "employed" ? "bg-emerald-600 text-white border-emerald-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:bg-emerald-50 hover:border-emerald-300"}`}>
                          <Briefcase className="h-3 w-3" /> Is Employee
                        </button>
                        <button type="button" onClick={() => handleEmploymentStatus(company.id, "not_employed")} disabled={company.isSaving}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border transition disabled:opacity-50 disabled:cursor-not-allowed ${company.employmentStatus === "not_employed" ? "bg-gray-600 text-white border-gray-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                          <X className="h-3 w-3" /> Not Employee
                        </button>
                      </div>
                    </div>
                  )}

                  {company.verificationStatus === "verified" && company.employmentStatus === "employed" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">Emp Code <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <Hash className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                          <input type="text" value={company.ecode} onChange={(e) => update(company.id, { ecode: e.target.value })} placeholder="EMP-001" className={`${inputCls} pl-6`} disabled={company.isSaving} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">Last Working Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none z-10" />
                          <CalendarInput value={company.lastWorkingDate} onChange={(val) => update(company.id, { lastWorkingDate: val })} disabled={company.isSaving} className="pl-6 text-xs py-1.5" />
                        </div>
                      </div>
                    </div>
                  )}

                  {company.verificationStatus === "not_verified" && (
                    <div className="flex items-center gap-2 pt-1">
                      <button type="button" onClick={() => handleSaveNotVerified(company)} disabled={company.isSaving}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-600 text-white hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
                        {company.isSaving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</> : <><XCircle className="h-3 w-3" /> Save as Not Verified</>}
                      </button>
                    </div>
                  )}

                  {company.verificationStatus === "verified" && company.employmentStatus === "not_employed" && (
                    <div className="flex items-center gap-2 pt-1">
                      <button type="button" onClick={() => handleSaveNotEmployedVerified(company)} disabled={company.isSaving}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
                        {company.isSaving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</> : <><Check className="h-3 w-3" /> Save as Not Employee & Verified</>}
                      </button>
                    </div>
                  )}

                  {company.verificationStatus === "verified" && company.employmentStatus === "employed" && (
                    <div className="flex items-center gap-2 pt-1">
                      <button type="button" onClick={() => handleSaveEmployedVerified(company)} disabled={company.isSaving || !company.ecode.trim()}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md shadow-sm ${!company.ecode.trim() ? "bg-gray-400 text-white cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700"} transition disabled:opacity-50 disabled:cursor-not-allowed`}>
                        {company.isSaving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</> : <><Briefcase className="h-3 w-3" /> Save as Is Employee & Verified</>}
                      </button>
                    </div>
                  )}

                  <InlineBanner msg={company.bannerMsg} type={company.bannerType} />
                </div>
              )}
            </>
            )}
          </div>
        ))}
      </div>

      <button
  type="button"
  onClick={canEditVerification ? addCompany : undefined}
  disabled={!canEditVerification}
  title={!canEditVerification ? "Not permitted" : undefined}
  className={`mt-3 w-full flex items-center justify-center gap-1 py-2 border-2 border-dashed rounded-md text-xs font-medium transition ${
    canEditVerification
      ? "border-blue-200 text-blue-500 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50/50"
      : "border-gray-200 text-gray-300 cursor-not-allowed"
  }`}
>
  <Plus className="h-3.5 w-3.5" /> Add Company
</button>
    </div>
  );
};

export default CompanyCheck;